import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { propertyService } from "@/lib/api";
import { memberService } from "@/lib/api/members";
import { filterNavByPermissions } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth";
import { usePropertyStore } from "@/stores/property";
import { useServiceStore } from "@/stores/service";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart3,
  BedDouble,
  BookOpen,
  Building2,
  CalendarRange,
  ClipboardList,
  ExternalLink,
  FileSignature,
  Grid3x3,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Menu,
  MessagesSquare,
  Plus,
  RadioTower,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const NAV = [
  { to: "/admin", label: "대시보드", icon: LayoutDashboard, end: true },
  { to: "/admin/rooms/board", label: "객실현황판", icon: Grid3x3, end: false, resource: "rooms" },
  {
    to: "/admin/reservations",
    label: "예약 · 프런트",
    icon: ClipboardList,
    end: false,
    resource: "reservations",
  },
  {
    to: "/admin/conversations",
    label: "컨시어지",
    icon: MessagesSquare,
    end: false,
    resource: "chat",
  },
  {
    to: "/admin/timeline",
    label: "타임라인",
    icon: CalendarRange,
    end: false,
    resource: "reservations",
  },
  { to: "/admin/rooms", label: "호실", icon: BedDouble, end: true, resource: "rooms" },
  { to: "/admin/room-types", label: "객실 타입", icon: LayoutGrid, end: false, resource: "rooms" },
  { to: "/admin/guides", label: "가이드 · FAQ", icon: BookOpen, end: false, resource: "guides" },
  { to: "/admin/contracts", label: "계약서", icon: FileSignature, end: false, resource: "reports" },
  {
    to: "/admin/properties",
    label: "숙소 관리",
    icon: Building2,
    end: false,
    resource: "properties",
  },
  { to: "/admin/channels", label: "채널", icon: RadioTower, end: false, resource: "channels" },
  { to: "/admin/reports", label: "리포트", icon: BarChart3, end: false, resource: "reports" },
  { to: "/admin/members", label: "멤버 · 권한", icon: Users, end: false, resource: "members" },
];

// Public docs, opened in a new tab so the operator keeps their console context.
const HELP_LINKS = [
  { href: "/guide", label: "사용 가이드" },
  { href: "/faq", label: "자주 묻는 질문" },
];

export function AdminShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const impersonation = useAuthStore((s) => s.impersonation);
  const stopImpersonation = useAuthStore((s) => s.stopImpersonation);
  const [mobileOpen, setMobileOpen] = useState(false);

  const selectedPropertyId = usePropertyStore((s) => s.selectedPropertyId);
  const setSelectedPropertyId = usePropertyStore((s) => s.setSelectedPropertyId);
  const { data: properties } = useQuery({
    queryKey: ["properties"],
    queryFn: propertyService.list,
  });

  const { data: myServices } = useQuery({
    queryKey: ["my-services"],
    queryFn: memberService.myServices,
  });
  const selectedServiceId = useServiceStore((s) => s.selectedServiceId);
  const setSelectedServiceId = useServiceStore((s) => s.setSelectedServiceId);
  const { data: myPerms } = useQuery({
    queryKey: ["my-permissions", selectedServiceId],
    queryFn: memberService.myPermissions,
  });

  // Keep the selection valid: auto-select the first property, drop stale ids.
  useEffect(() => {
    if (!properties) return;
    if (properties.length === 0) {
      if (selectedPropertyId) setSelectedPropertyId(null);
      return;
    }
    if (!selectedPropertyId || !properties.some((p) => p.id === selectedPropertyId)) {
      setSelectedPropertyId(properties[0].id);
    }
  }, [properties, selectedPropertyId, setSelectedPropertyId]);

  // Self-heal: drop a selected service id that no longer belongs to this account
  // (e.g. removed as a member, or a different account logged in on this browser).
  useEffect(() => {
    if (!myServices) return;
    if (selectedServiceId && !myServices.some((s) => s.id === selectedServiceId)) {
      setSelectedServiceId(null);
    }
  }, [myServices, selectedServiceId, setSelectedServiceId]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const propertyPicker =
    properties && properties.length === 0 ? (
      <Button asChild variant="outline" className="w-full justify-start gap-2">
        <NavLink to="/admin/properties" onClick={() => setMobileOpen(false)}>
          <Plus className="h-4 w-4" /> 숙소 등록
        </NavLink>
      </Button>
    ) : (
      <Select value={selectedPropertyId ?? ""} onValueChange={(id) => setSelectedPropertyId(id)}>
        <SelectTrigger aria-label="숙소 선택">
          <SelectValue placeholder="숙소 선택…" />
        </SelectTrigger>
        <SelectContent>
          {(properties ?? []).map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );

  const activeServiceId = selectedServiceId ?? myServices?.find((s) => s.is_owner)?.id ?? "";
  const servicePicker =
    myServices && myServices.length > 1 ? (
      <Select
        value={activeServiceId}
        onValueChange={(id) => {
          setSelectedServiceId(id);
          // 서비스가 바뀌면 모든 서버 상태(숙소 선택 포함)가 무효 — 전체 리로드가 가장 안전.
          usePropertyStore.getState().setSelectedPropertyId(null);
          window.location.assign("/admin");
        }}
      >
        <SelectTrigger aria-label="서비스 선택">
          <SelectValue placeholder="서비스 선택…" />
        </SelectTrigger>
        <SelectContent>
          {myServices.map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {s.name}
              {s.is_owner
                ? ""
                : ` (${s.role === "viewer" ? "뷰어" : s.role === "manager" ? "매니저" : "커스텀"})`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : null;

  // Superadmin ops console — only for emails on the server-side env whitelist.
  const navItems = filterNavByPermissions(
    user?.is_superadmin
      ? [...NAV, { to: "/admin/super", label: "슈퍼어드민", icon: ShieldCheck, end: false }]
      : NAV,
    myPerms?.permissions
  );

  const nav = (
    <nav className="flex flex-1 flex-col gap-1">
      {navItems.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={() => setMobileOpen(false)}
          className={({ isActive }) =>
            cn(
              "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "bg-primary/10 text-primary"
                : "text-foreground/70 hover:bg-secondary hover:text-foreground"
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                className={cn(
                  "absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-opacity",
                  isActive ? "opacity-100" : "opacity-0"
                )}
              />
              <Icon className="h-4 w-4" />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );

  const sidebarBody = (
    <div className="flex h-full flex-col gap-6 p-4">
      <div className="px-1 pt-1">
        <Logo size={38} withWordmark />
      </div>
      {servicePicker ? <div className="px-1">{servicePicker}</div> : null}
      <div className="px-1">{propertyPicker}</div>
      {nav}
      <div className="pt-3">
        <div className="hairline mb-3" />
        {HELP_LINKS.map(({ href, label }) => (
          <a
            key={href}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-foreground/70 transition-colors hover:bg-secondary hover:text-foreground"
          >
            <ExternalLink className="h-4 w-4" />
            {label}
          </a>
        ))}
        <div className="hairline my-3" />
        {user ? (
          <div className="mb-2 px-2">
            <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
        ) : null}
        <Button variant="ghost" className="w-full justify-start gap-3" onClick={handleLogout}>
          <LogOut className="h-4 w-4" />
          로그아웃
        </Button>
      </div>
    </div>
  );

  return (
    <div className="bg-atmosphere flex min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border/60 bg-card/80 backdrop-blur-sm md:block">
        {sidebarBody}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <button
            type="button"
            aria-label="메뉴 닫기"
            className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-64 border-r border-border/70 bg-card shadow-float">
            {sidebarBody}
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {impersonation ? (
          <div
            data-testid="impersonation-banner"
            className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-sm font-medium text-white"
          >
            <span className="truncate">
              🔓 {impersonation.admin.name} ({impersonation.admin.email}) 계정으로 로그인됨 ·
              임퍼소네이션
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 shrink-0 bg-white/90 text-xs text-amber-900 hover:bg-white"
              onClick={() => {
                stopImpersonation();
                // 전체 리로드로 대상 계정의 쿼리 캐시를 버리고 원래 계정으로 복귀.
                window.location.assign("/admin/super");
              }}
            >
              원래 계정으로
            </Button>
          </div>
        ) : null}
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border/60 bg-card/80 px-4 py-3 backdrop-blur-md md:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label={mobileOpen ? "메뉴 닫기" : "메뉴 열기"}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <Logo size={28} withWordmark />
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="mx-auto w-full max-w-5xl animate-rise">{children}</div>
        </main>
      </div>
    </div>
  );
}
